import { Stack, Typography, Box, Fade } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DefaultLoader from "components/common/DefaultLoader";

const CentredLoader = ({
  label,
  showProgress = false,
  useEnhancedAnimation = false,
  error = false,
  extraComponents = null,
}) => {
  const theme = useTheme();
  const hasLabel = label != null;

  if (!useEnhancedAnimation) {
    return (
      <Stack
        alignItems={"center"}
        justifyContent={"center"}
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          zIndex: 1000,
        }}
      >
        {!error && (
          <DefaultLoader
            type={"TailSpin"}
            size={50}
            style={{ marginBottom: hasLabel ? "16px" : "0" }}
          />
        )}
        {error && (
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              backgroundColor: "#f44336",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: hasLabel ? "16px" : "0",
              color: theme.brand.white,
              fontSize: "24px",
              fontWeight: "bold",
            }}
          >
            {"!"}
          </Box>
        )}
        {hasLabel && (
          <Typography
            variant={"h6"}
            color={error ? "error" : "primary"}
            sx={{
              fontWeight: 500,
              textAlign: "center",
              minWidth: "200px",
              minHeight: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {label}
          </Typography>
        )}
        {extraComponents}
      </Stack>
    );
  }

  return (
    <Stack
      alignItems={"center"}
      justifyContent={"center"}
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(2px)",
        zIndex: 1000,
      }}
    >
      <Fade in timeout={300}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: 4,
            borderRadius: 3,
            backgroundColor: theme.brand.white,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            border: error
              ? "1px solid #f44336"
              : "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 80,
              height: 80,
              marginBottom: hasLabel ? 3 : 0,
            }}
          >
            {!error && (
              <>
                <Box
                  sx={{
                    position: "absolute",
                    width: 72,
                    height: 72,
                    border: "3px solid #f0f0f0",
                    borderTop: `3px solid ${theme.brand.accent[500]}`,
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    width: 56,
                    height: 56,
                    border: "2px solid #f8f8f8",
                    borderBottom: `2px solid ${theme.brand.accent[400]}`,
                    borderRadius: "50%",
                    animation: "spin-reverse 1.5s linear infinite",
                    "@keyframes spin-reverse": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(-360deg)" },
                    },
                  }}
                />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    backgroundColor: theme.brand.accent[500],
                    borderRadius: "50%",
                    animation: "pulse 2s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%, 100%": { opacity: 1, transform: "scale(1)" },
                      "50%": { opacity: 0.7, transform: "scale(1.2)" },
                    },
                  }}
                />
              </>
            )}
            {error && (
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  backgroundColor: "#f44336",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.brand.white,
                  fontSize: "32px",
                  fontWeight: "bold",
                }}
              >
                {"!"}
              </Box>
            )}
          </Box>

          {hasLabel && (
            <Typography
              variant={"h6"}
              color={error ? "error" : "primary"}
              sx={{
                fontWeight: 500,
                textAlign: "center",
                minWidth: "200px",
                minHeight: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {label}
            </Typography>
          )}

          {showProgress && !error && (
            <Typography
              variant={"body2"}
              color={"text.secondary"}
              sx={{
                mt: 1,
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              {"This may take a few moments"}
            </Typography>
          )}
          {extraComponents}
        </Box>
      </Fade>
    </Stack>
  );
};

export default CentredLoader;
